import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { LoadingSpinner } from "../common/LoadingSpinner";
import { Button } from "../common/Button";

const CHECK_PAYMENT_STATUS_BASES = [
  import.meta.env.VITE_BACKEND_API_BASE_URL,
  import.meta.env.VITE_AUTH_API_BASE_URL,
  "https://gainwisefx.com/api/",
  "/api",
].filter((value, index, list) => value && list.indexOf(value) === index);

const PaymentStatus = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const orderTrackingId = searchParams.get("OrderTrackingId");
  const merchantReference = searchParams.get("OrderMerchantReference");

  useEffect(() => {
    if (orderTrackingId || merchantReference) {
      checkPaymentStatus();
    } else {
      setError("Invalid payment callback - missing tracking information");
      setLoading(false);
    }
  }, [orderTrackingId, merchantReference]);

  const checkPaymentStatus = async () => {
    try {
      const params = new URLSearchParams();
      if (orderTrackingId) params.append("order_tracking_id", orderTrackingId);
      if (merchantReference)
        params.append("merchant_reference", merchantReference);

      let data = null;

      for (const baseUrl of CHECK_PAYMENT_STATUS_BASES) {
        try {
          const response = await fetch(
            `${baseUrl}/check-payment-status.php?${params.toString()}`
          );

          if (!response.ok) {
            continue;
          }

          data = await response.json();
          break;
        } catch {
          continue;
        }
      }

      if (!data) {
        throw new Error("Network error");
      }

      if (data.success) {
        setPayment(data.payment);
      } else {
        setError(data.error || "Failed to check payment status");
      }
    } catch (err) {
      console.error("Payment status check error:", err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return (
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
            <svg
              className="h-6 w-6 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        );
      case "failed":
        return (
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
        );
      default:
        return (
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
            <svg
              className="h-6 w-6 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        );
    }
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case "completed":
        return {
          title: "Payment Successful!",
          message: "Your payment has been processed successfully.",
          color: "text-green-600",
        };
      case "failed":
        return {
          title: "Payment Failed",
          message: "Your payment could not be processed. Please try again.",
          color: "text-red-600",
        };
      default:
        return {
          title: "Payment Processing",
          message: "Your payment is being processed. Please wait...",
          color: "text-yellow-600",
        };
    }
  };

  const handleRetry = () => {
    navigate("/payment", { replace: true });
  };

  const handleGoHome = () => {
    navigate("/dashboard", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Checking payment status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Error</h3>
          <p className="mt-2 text-sm text-gray-500">{error}</p>
          <div className="mt-6 space-y-3">
            <Button onClick={checkPaymentStatus} className="w-full">
              Try Again
            </Button>
            <Button onClick={handleGoHome} variant="outline" className="w-full">
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600">No payment information found.</p>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusMessage(payment.status);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
        <div className="text-center">
          {getStatusIcon(payment.status)}
          <h3 className={`mt-4 text-lg font-medium ${statusInfo.color}`}>
            {statusInfo.title}
          </h3>
          <p className="mt-2 text-sm text-gray-500">{statusInfo.message}</p>
        </div>

        <div className="mt-6 border-t border-gray-200 pt-6">
          <dl className="space-y-3">
            <div className="flex justify-between text-sm">
              <dt className="text-gray-500">Amount:</dt>
              <dd className="font-medium text-gray-900">
                {payment.currency} {payment.amount}
              </dd>
            </div>
            <div className="flex justify-between text-sm">
              <dt className="text-gray-500">Reference:</dt>
              <dd className="font-medium text-gray-900 text-xs">
                {payment.merchant_reference}
              </dd>
            </div>
            <div className="flex justify-between text-sm">
              <dt className="text-gray-500">Email:</dt>
              <dd className="font-medium text-gray-900">
                {payment.customer_email}
              </dd>
            </div>
            <div className="flex justify-between text-sm">
              <dt className="text-gray-500">Description:</dt>
              <dd className="font-medium text-gray-900">
                {payment.description}
              </dd>
            </div>
            <div className="flex justify-between text-sm">
              <dt className="text-gray-500">Date:</dt>
              <dd className="font-medium text-gray-900">
                {new Date(payment.created_at).toLocaleDateString()}
              </dd>
            </div>
          </dl>
        </div>

        <div className="mt-6 space-y-3">
          {payment.status === "failed" && (
            <Button onClick={handleRetry} className="w-full">
              Try Again
            </Button>
          )}
          {payment.status === "pending" && (
            <Button
              onClick={checkPaymentStatus}
              variant="outline"
              className="w-full"
            >
              Refresh Status
            </Button>
          )}
          <Button
            onClick={handleGoHome}
            variant={payment.status === "completed" ? "primary" : "outline"}
            className="w-full"
          >
            {payment.status === "completed" ? "Continue" : "Go to Dashboard"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentStatus;
